import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Quiz as QuizIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuiz } from '../../contexts/QuizContext';
import documentService, { Document as DocumentType } from '../../services/documentService';
import questionService, { Question, QuestionListResponse } from '../../services/questionService';
import QuizCreator from '../../components/quiz/QuizCreator';
import ContentRenderer from '../../components/common/ContentRenderer';
import { getFileTypeName, renderFileTypeIcon, formatDate } from '../../config/fileUtils';

const QuizCreation: React.FC = () => {
  const navigate = useNavigate();
  const { quizError } = useQuiz();
  
  // State for documents
  const [documents, setDocuments] = useState<DocumentType[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState<boolean>(true);
  const [documentsError, setDocumentsError] = useState<string | null>(null);

  // State for selected document and its questions
  const [selectedDocument, setSelectedDocument] = useState<DocumentType | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState<boolean>(false);
  const [questionsError, setQuestionsError] = useState<string | null>(null);

  // State for quiz creation
  const [showCreator, setShowCreator] = useState(false);

  // Fetch documents on component mount
  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setDocumentsLoading(true);
    setDocumentsError(null);
    try {
      const response = await documentService.getDocuments();
      // Only show processed documents that have questions
      const processedDocs = response.documents.filter(doc => doc.processed);
      setDocuments(processedDocs);
    } catch (err: any) {
      setDocumentsError(err.message || 'Failed to fetch documents');
    } finally {
      setDocumentsLoading(false);
    }
  };

  const fetchQuestionsForDocument = async (document: DocumentType) => {
    setQuestionsLoading(true);
    setQuestionsError(null);
    try {
      const response: QuestionListResponse = await questionService.getQuestions(document.id);
      setQuestions(response.questions);
      setSelectedDocument(document);
    } catch (err: any) {
      setQuestionsError(err.message || 'Failed to fetch questions');
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleDocumentSelect = (document: DocumentType) => {
    fetchQuestionsForDocument(document);
  };

  const handleBackToDocuments = () => {
    setSelectedDocument(null);
    setQuestions([]);
    setQuestionsError(null);
  };

  const handleCreateSuccess = (quizId: number) => {
    // Navigate to quiz detail or list after successful creation
    navigate('/quizzes');
  };





  // Show document list if no document is selected
  if (!selectedDocument) {
    return (
      <Container maxWidth="xl">
        <Box py={3}>
          <Typography variant="h4" gutterBottom>
            Tạo bài tập trắc nghiệm
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Chọn tài liệu để tạo bài tập từ các câu hỏi có sẵn
          </Typography>

          {/* Instructions */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Hướng dẫn tạo bài tập
              </Typography>
              <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
                <Box display="flex" alignItems="center" flex={1}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      fontSize: '14px',
                      fontWeight: 'bold',
                    }}
                  >
                    1
                  </Box>
                  <Typography variant="body2">
                    <strong>Chọn tài liệu:</strong> Chọn tài liệu chứa câu hỏi bạn muốn tạo bài tập
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" flex={1}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      fontSize: '14px',
                      fontWeight: 'bold',
                    }}
                  >
                    2
                  </Box>
                  <Typography variant="body2">
                    <strong>Chọn câu hỏi:</strong> Chọn các câu hỏi phù hợp từ tài liệu
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" flex={1}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      fontSize: '14px',
                      fontWeight: 'bold',
                    }}
                  >
                    3
                  </Box>
                  <Typography variant="body2">
                    <strong>Tạo bài tập:</strong> Thiết lập thông tin và cài đặt bài tập
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {documentsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {documentsError}
            </Alert>
          )}

          {documentsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : documents.length === 0 ? (
            <Alert severity="info">
              Không có tài liệu nào đã được xử lý. Vui lòng upload và xử lý tài liệu trước.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Tài liệu</TableCell>
                    <TableCell>Loại file</TableCell>
                    <TableCell>Ngày tạo</TableCell>
                    <TableCell>Trạng thái</TableCell>
                    <TableCell align="center">Thao tác</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((document) => (
                    <TableRow key={document.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {renderFileTypeIcon(document.file_type)}
                          <Typography variant="body2" fontWeight="medium">
                            {document.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {getFileTypeName(document.file_type)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(document.created_at, 'vi-VN')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label="Đã xử lý" 
                          color="success" 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleDocumentSelect(document)}
                          color="primary"
                          title="Tạo bài tập từ tài liệu này"
                        >
                          <QuizIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Container>
    );
  }

  // Show quiz creation for selected document
  return (
    <Container maxWidth="xl">
      <Box py={3}>
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            component="button" 
            variant="body1" 
            onClick={handleBackToDocuments}
            underline="hover"
          >
            Tạo bài tập
          </Link>
          <Typography color="text.primary">
            {selectedDocument.name}
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={handleBackToDocuments}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" gutterBottom>
                Tạo bài tập từ: {selectedDocument.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tổng số câu hỏi có sẵn: {questions.length}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCreator(true)}
            disabled={questions.length === 0}
            size="large"
          >
            Tạo bài tập
          </Button>
        </Box>

        {/* Error display */}
        {questionsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {questionsError}
          </Alert>
        )}

        {quizError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {quizError}
          </Alert>
        )}

        {/* Loading or Questions Preview */}
        {questionsLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : questions.length === 0 ? (
          <Alert severity="info">
            Tài liệu này chưa có câu hỏi nào. Vui lòng xử lý tài liệu để tạo câu hỏi trước.
          </Alert>
        ) : (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Xem trước câu hỏi ({questions.length} câu)
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Dưới đây là các câu hỏi có sẵn từ tài liệu này. Bạn có thể chọn các câu hỏi phù hợp khi tạo bài tập.
              </Typography>
              
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {questions.slice(0, 5).map((question, index) => (
                  <Box key={question.id} sx={{ mb: 2, p: 2, border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom component="div">
                      Câu {index + 1}: <ContentRenderer text={question.text} />
                    </Typography>
                    <Box sx={{ ml: 2 }}>
                      {question.options.map((option, optionIndex) => (
                        <Typography 
                          key={optionIndex} 
                          variant="body2" 
                          component="div"
                          color={question.correct_answer === (optionIndex + 1) ? 'success.main' : 'text.secondary'}
                          sx={{ mb: 0.5 }}
                        >
                          {String.fromCharCode(65 + optionIndex)}. <ContentRenderer text={option} />
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
                {questions.length > 5 && (
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    ... và {questions.length - 5} câu hỏi khác
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Quiz Creator Dialog */}
        <QuizCreator
          open={showCreator}
          onClose={() => setShowCreator(false)}
          onSuccess={handleCreateSuccess}
          preSelectedDocument={selectedDocument}
        />
      </Box>
    </Container>
  );
};

export default QuizCreation; 